import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, unref as _unref, createVNode as _createVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, resolveDynamicComponent as _resolveDynamicComponent, createBlock as _createBlock } from "vue"

const _hoisted_1 = {
  key: 0,
  id: "app-sidebar-vue",
  class: "app-sidebar reduced"
}
const _hoisted_2 = {
  key: 0,
  class: "top-info"
}
const _hoisted_3 = { class: "title" }
const _hoisted_4 = ["innerHTML"]

import { ref, computed, onMounted, nextTick, useTemplateRef, defineCustomElement } from 'vue';

import NcActions from '@nextcloud/vue/components/NcActions';
import NcActionButton from '@nextcloud/vue/components/NcActionButton';
import NcAppSidebar from '@nextcloud/vue/components/NcAppSidebar';
import NcAppSidebarTab from '@nextcloud/vue/components/NcAppSidebarTab';
import { File, Folder, getSidebarTabs, registerSidebarTab } from '@nextcloud/files';
import {
  getClient,
  getDefaultPropfind,
  getRemoteURL,
  getRootPath,
  registerDavProperty,
  resultToNode,
} from '@nextcloud/files/dav';
import { translate as t } from '@services/l10n';

import Metadata from '@components/Metadata.vue';
import SidebarSubtitle from '@components/SidebarSubtitle.vue';
import XLoadingIcon from '@components/XLoadingIcon.vue';
import router from '@services/router';

import { windowDims } from '@services/viewport';
import { fragment } from '@services/route-fragment';
import * as utils from '@services/utils/common';

import type { IImageInfo, IPhoto } from '@typings';
import type { IFolder, INode, ISidebarContext, ISidebarTab, IView } from '@nextcloud/files';
import type { FileStat, ResponseDataDetailed } from 'webdav';

import CloseIcon from 'vue-material-design-icons/Close.vue';
import InfoSvg from '@assets/info.svg';

const SIDEBAR_TAB_ID = 'memories-metadata';
const SIDEBAR_TAG_NAME = 'memories-files-sidebar-tab';


export default /*@__PURE__*/_defineComponent({
  __name: 'Sidebar',
  setup(__props) {

if (!getSidebarTabs().some((tab) => tab.id === SIDEBAR_TAB_ID)) {
  registerSidebarTab({
    id: SIDEBAR_TAB_ID,
    order: 50,
    displayName: t('memories', 'Info'),
    iconSvgInline: window.atob(InfoSvg.split(',')[1]), // base64 to svg
    enabled: () => true,
    tagName: SIDEBAR_TAG_NAME,
    async onInit() {
      if (window.customElements.get(SIDEBAR_TAG_NAME)) {
        // element already defined
        return;
      }
      const { default: MetadataTab } = await import('@components/Metadata.vue');
      window.customElements.define(
        SIDEBAR_TAG_NAME,
        defineCustomElement(MetadataTab, {
          configureApp: (app) => {
            app.use(router);
          },
          shadowRoot: false,
        }),
      );
    },
  });
}

const metadataRef = useTemplateRef<InstanceType<typeof Metadata>>('metadata');

const nativeOpen = ref(false);
const reducedOpen = ref(false);
const info = ref<null | IImageInfo>(null);
const lastKnownWidth = ref(0);
const pendingTab = ref<string | null>(null);
const nativeTab = ref<ISidebarTab | null>(null);
const nativeNode = ref<INode | null>(null);
const nativeFolder = ref<IFolder | null>(null);
const nativeView = ref<IView | null>(null);
const readyTabs = ref(new Set<string>());

/** Tabs from the global registry enabled for the current native node */
const availableTabs = computed((): ISidebarTab[] => {
  if (!nativeNode.value || !nativeFolder.value || !nativeView.value) return [];
  const context: ISidebarContext = {
    node: nativeNode.value,
    folder: nativeFolder.value,
    view: nativeView.value,
  };
  return getSidebarTabs()
    .filter((tab) => {
      try {
        return !tab.enabled || tab.enabled(context);
      } catch (e) {
        return false;
      }
    })
    .sort((a, b) => a.order - b.order);
});

onMounted(() => {
  _m.sidebar = {
    open,
    close,
    isOpen,
    setTab,
    invalidateUnless,
    getWidth,
  };

  // Remove after https://github.com/nextcloud/server/pull/51077
  registerDavProperty('nc:share-attributes');
});

utils.useBus('memories:fragment:pop:sidebar', close);

async function open(photo: IPhoto | number, filename?: string, useNative = false) {
  if ((!photo || useNative) && filename && (await openNative(photo, filename))) {
    return;
  }

  if (!photo) return;

  // Open reduced sidebar
  nativeOpen.value = false;
  nativeTab.value = null;
  reducedOpen.value = true;
  await nextTick();

  // Update metadata compoenent
  try {
    const metadata = await metadataRef.value?.update(photo);
    if (!metadata) return;
    info.value = metadata;
  } catch {
    return;
  }
  handleOpen();
}

/**
 * Show a native sidebar tab for the given file.
 * @returns true if a native tab was shown
 */
async function openNative(photo: IPhoto | number, filename: string): Promise<boolean> {
  // Resolve the requested tab first to avoid a WebDAV roundtrip
  // when it isn't registered (falls back to reduced below).
  const wanted = pendingTab.value ?? nativeTab.value?.id;
  if (wanted && !getSidebarTabs().some((tab) => tab.id === wanted)) {
    console.warn(`Not showing native sidebar tab: '${wanted}' is not registered`);
    pendingTab.value = null;
    return false;
  }

  const fileid = typeof photo === 'number' ? photo : photo?.fileid;
  const photoObj = typeof photo === 'object' ? photo : undefined;

  // Resolve a full node via WebDAV so tabs get permissions, mime, etc.
  let node: INode | null = null;
  try {
    const res = (await getClient().stat(`${getRootPath()}${filename}`, {
      data: getDefaultPropfind(),
      details: true,
    })) as ResponseDataDetailed<FileStat>;
    node = resultToNode(res.data, getRootPath(), getRemoteURL());
  } catch (e) {
    // Fall back to a minimal node if the fileid is known
    if (!fileid) {
      console.warn('Not showing native sidebar tab: cannot resolve', filename);
      return false;
    }
    node = new File({
      source: `${getRemoteURL()}${getRootPath()}${filename}`,
      id: fileid,
      root: getRootPath(),
      owner: utils.uid,
      mime: photoObj?.mimetype,
      displayname: photoObj?.basename,
    });
  }

  const dir = filename.slice(0, filename.lastIndexOf('/')) || '/';
  const folder: IFolder = new Folder({
    source: `${getRemoteURL()}${getRootPath()}${dir}`,
    root: getRootPath(),
    owner: node.owner,
  });
  const view: IView = {
    id: 'memories',
    name: 'Memories',
    icon: '',
    getContents: async () => ({ contents: [], folder }),
  };

  nativeNode.value = node;
  nativeFolder.value = folder;
  nativeView.value = view;

  const tab =
    availableTabs.value.find((t) => t.id === wanted) ??
    availableTabs.value.find((t) => t.id === nativeTab.value?.id) ??
    availableTabs.value[0];
  if (!tab) {
    console.warn('Not showing native sidebar: no enabled tabs for', filename);
    nativeNode.value = null;
    nativeFolder.value = null;
    nativeView.value = null;
    return false;
  }

  nativeTab.value = tab;
  pendingTab.value = null;
  info.value = {
    basename: node.displayname,
    size: node.size ?? 0,
    mtime: (node.mtime?.getTime() ?? 0) / 1000,
  } as IImageInfo;

  reducedOpen.value = false;
  nativeOpen.value = true;
  void initNativeTab(tab);
  // Wait for the shell to render so getWidth() measures correctly
  // and the viewer resizes instead of being overlaid.
  await nextTick();
  handleOpen();
  return true;
}

/** Lazily initialize a tab's web component, mirroring the Files app */
async function initNativeTab(tab: ISidebarTab) {
  try {
    if (!window.customElements.get(tab.tagName)) {
      await tab.onInit?.();
      await window.customElements.whenDefined(tab.tagName);
    }
    if (nativeTab.value?.tagName === tab.tagName) {
      readyTabs.value.add(tab.tagName);
    }
  } catch (e) {
    if (window.customElements.get(tab.tagName) && nativeTab.value?.tagName === tab.tagName) {
      readyTabs.value.add(tab.tagName);
    } else {
      console.warn(`Failed to initialize native sidebar tab '${tab.id}': `, e);
    }
  }
}

async function close() {
  if (nativeOpen.value || nativeTab.value) {
    nativeOpen.value = false;
    nativeTab.value = null;
    nativeNode.value = null;
    nativeFolder.value = null;
    nativeView.value = null;
    pendingTab.value = null;
    // Wait for teardown so getWidth() no longer measures the shell.
    await nextTick();
  }
  if (reducedOpen.value) {
    reducedOpen.value = false;
    await nextTick();
  }
  handleClose();
}

function isOpen() {
  return reducedOpen.value || nativeOpen.value;
}

function setTab(tab: string) {
  pendingTab.value = tab;
  if (nativeOpen.value) {
    const found = availableTabs.value.find((t) => t.id === tab);
    if (found) {
      nativeTab.value = found;
      void initNativeTab(found);
    } else {
      console.warn(`Not showing native sidebar tab: '${tab}' is not available`);
    }
  }
}

function invalidateUnless(fileid: number) {
  metadataRef.value?.invalidateUnless(fileid);
}

function getWidth() {
  const sidebar = document.getElementById('app-sidebar-vue') ?? document.getElementById('app-sidebar-native');
  lastKnownWidth.value = sidebar?.offsetWidth || lastKnownWidth.value;
  return (lastKnownWidth.value || 2) - 2;
}

function handleClose() {
  utils.bus.emit('memories:sidebar:closed', null);
  fragment.pop(fragment.types.sidebar);
}

function handleOpen() {
  // Stop sidebar typing from leaking outside
  const sidebar = document.getElementById('app-sidebar-vue') ?? document.getElementById('app-sidebar-native');
  sidebar?.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === 'Tab') {
      e.stopPropagation();
      return;
    }

    const element = e.target as HTMLElement;
    if (element.tagName === 'INPUT' || element.tagName === 'TEXTAREA' || element.isContentEditable) {
      e.stopPropagation();
      return;
    }
  });

  // Emit event
  utils.bus.emit('memories:sidebar:opened', null);

  // Use fragment navigation only on mobile
  if (windowDims.isMobile) {
    fragment.push(fragment.types.sidebar);
  }
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    (reducedOpen.value)
      ? (_openBlock(), _createElementBlock("aside", _hoisted_1, [
          (info.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
                _createElementVNode("div", _hoisted_3, [
                  _createElementVNode("h2", null, _toDisplayString(info.value.basename), 1),
                  _createVNode(_unref(NcActions), { inline: 1 }, {
                    default: _withCtx(() => [
                      _createVNode(_unref(NcActionButton), {
                        "aria-label": _unref(t)('memories', 'Close'),
                        onClick: _cache[0] || (_cache[0] = ($event: any) => (close()))
                      }, {
                        icon: _withCtx(() => [
                          _createVNode(CloseIcon, { size: 20 })
                        ]),
                        default: _withCtx(() => [
                          _createTextVNode(_toDisplayString(_unref(t)('memories', 'Close')) + " ", 1)
                        ]),
                        _: 1
                      }, 8, ["aria-label"])
                    ]),
                    _: 1
                  })
                ]),
                _createVNode(SidebarSubtitle, {
                  size: info.value.size,
                  mtime: info.value.mtime
                }, null, 8, ["size", "mtime"])
              ]))
            : _createCommentVNode("", true),
          _createVNode(Metadata, { ref: "metadata" }, null, 512)
        ]))
      : _createCommentVNode("", true),
    (nativeOpen.value)
      ? (_openBlock(), _createBlock(_unref(NcAppSidebar), {
          key: 1,
          id: "app-sidebar-native",
          open: nativeOpen.value,
          active: nativeTab.value?.id ?? '',
          name: nativeNode.value?.displayname ?? '',
          "no-toggle": "",
          onClose: close,
          "onUpdate:active": setTab
        }, {
          subname: _withCtx(() => [
            _createVNode(SidebarSubtitle, {
              size: info.value?.size ?? 0,
              mtime: info.value?.mtime ?? 0
            }, null, 8, ["size", "mtime"])
          ]),
          default: _withCtx(() => [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(availableTabs.value, (tab) => {
              return (_openBlock(), _createBlock(_unref(NcAppSidebarTab), {
                key: tab.id,
                id: tab.id,
                name: tab.displayName,
                order: tab.order
              }, {
                icon: _withCtx(() => [
                  _createElementVNode("span", {
                    class: "svg-icon",
                    innerHTML: tab.iconSvgInline
                  }, null, 8, _hoisted_4)
                ]),
                default: _withCtx(() => [
                  (readyTabs.value.has(tab.tagName))
                    ? (_openBlock(), _createBlock(_resolveDynamicComponent(tab.tagName), {
                        key: 0,
                        ".active": nativeTab.value?.id === tab.id,
                        ".node": nativeNode.value,
                        ".folder": nativeFolder.value,
                        ".view": nativeView.value
                      }, null, 40, [".active", ".node", ".folder", ".view"]))
                    : (_openBlock(), _createBlock(XLoadingIcon, { key: 1 }))
                ]),
                _: 2
              }, 1032, ["id", "name", "order"]))
            }), 128))
          ]),
          _: 1
        }, 8, ["open", "active", "name"]))
      : _createCommentVNode("", true)
  ], 64))
}
}

})